import { useCallback, useEffect, useState } from 'react'

import {
  listCheckpoints, createCheckpoint, restoreCheckpoint, hardResetCheckpoint,
  listBranches, createBranch, closeBranch, openBranch, deleteBranch,
  getChanges, diffCheckpoints, previewMerge, applyMerge,
} from '../../lib/assetManagerApi.js'
import { shortId, initials, colorForUser } from '../../lib/versionHistory.js'
import { S, C, changeColor, fmtDate, fmtTime } from './vcStyles.js'
import { groupCheckpointsByDate } from '../../lib/versionHistory.js'
import BranchList from './BranchList.jsx'
import CheckpointList from './CheckpointList.jsx'
import GraphView from './GraphView.jsx'
import FloatingWindow from '../FloatingWindow.jsx'
import ContextMenu from './ContextMenu.jsx'
import {
  RestoreFlow, HardResetFlow, NewBranchFlow, NewCheckpointFlow, DiffFlow, MergeFlow,
} from './SidePanel.jsx'

// VersionPanel — the content-browser version-control window (ENG-539), built
// for parity with the PlayCanvas editor: a large centered modal with a two-pane
// body (branches | checkpoints), side-panel confirmation flows (Restore / Hard
// reset / New branch / New checkpoint / Merge), a Compare picker, and a
// near-fullscreen commit graph.
//
// Server is the single source of truth (CLAUDE.md): every list/diff/merge is
// fetched from the asset-manager; failures surface loudly, never faked.
//
// Our backend has no server-side "checked-out branch"; the selected branch in
// the left pane is the working branch — new checkpoints attach to it and
// restore/hard-reset act on it. Restore reconciles the global live state.
// ── Panel sub-components ──────────────────────────────────────────────────
// Defined at MODULE scope on purpose: components declared inside VersionPanel
// get a new identity on every parent render, so React remounts their whole
// subtree — any pointer press spanning an ambient re-render (presence pings,
// asset notifications) then lands on a destroyed node and the click is
// swallowed. Stable identity keeps the DOM alive across re-renders.
// ── branch dropdown ──────────────────────────────────────
const BranchDropdown = ({ branches, currentBranchId, busy, onSelect, onNewBranch, onMerge, onToggleClosed, onDelete }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const current = branches.find((b) => b.id === currentBranchId)
  const [menu, setMenu] = useState(null)

  return (
    <>
      <button
        style={{ ...S.ghost, fontSize: 12 }}
        onClick={() => setDropdownOpen((v) => !v)}
      >
        {current?.name || '—'}
      </button>
      {dropdownOpen && (
        <div style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 2100,
              background: C.panel,
              border: `1px solid ${C.borderInput}`,
              borderRadius: 6,
              padding: '4px 0',
              minWidth: 200,
              boxShadow: '0 12px 40px rgba(0,0,0,.5)',
            }}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            {branches.map((b) => (
              <div key={b.id}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                    background: b.id === currentBranchId ? C.accentSoft : 'transparent',
                  }}
                  onClick={() => { onSelect(b); setDropdownOpen(false) }}
                >
                  <span
                    style={{
                      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                      background: b.id === currentBranchId ? C.added : b.closed ? C.textDim : C.accent,
                    }}
                  />
                  <span style={{ flex: 1 }}>{b.permanent ? '★ ' : ''}{b.name}</span>
                  <button
                    style={{ ...S.iconBtn, fontSize: 11 }}
                    title="Branch actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      const r = e.currentTarget.getBoundingClientRect()
                      setMenu({ x: r.right, y: r.bottom, branch: b })
                    }}
                  >
                    ⋯
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            { label: 'Switch to this branch', hidden: menu.branch.id === currentBranchId || menu.branch.closed, onClick: () => { onSelect(menu.branch); setDropdownOpen(false) } },
            { label: 'New branch from here', onClick: () => { onNewBranch(menu.branch); setMenu(null) } },
            { label: 'Merge into current branch', hidden: menu.branch.id === currentBranchId || menu.branch.closed, onClick: () => { onMerge(menu.branch); setMenu(null) } },
            { separator: true },
            { label: menu.branch.closed ? 'Re-open this branch' : 'Close this branch', hidden: menu.branch.permanent, onClick: () => { onToggleClosed(menu.branch); setMenu(null) } },
            { label: 'Delete this branch', danger: true, hidden: menu.branch.permanent || menu.branch.id === currentBranchId, onClick: () => { onDelete(menu.branch); setMenu(null) } },
          ]}
        />
      )}
    </>
  )
}

// ── Inline component: Changes tab ──────────────────────────────────────────
const ChangesTabContent = ({ changes, loading }) => {
  if (loading) return <div style={S.empty}>Loading…</div>
  if (changes === null) return <div style={S.empty}>Open the Changes tab to see uncheckpointed edits.</div>
  if (changes.length === 0) return <div style={S.empty}>No changes since the last checkpoint.</div>
  const s = { added: 0, modified: 0, removed: 0 }
  for (const c of changes) if (s[c.type] != null) s[c.type] += 1
  return (
    <div style={{ padding: 14 }}>
      <div style={S.diffBox}>
        <div style={{ marginBottom: 6 }}>
          <span style={{ color: C.added }}>+{s.added}</span>{' '}
          <span style={{ color: C.modified }}>~{s.modified}</span>{' '}
          <span style={{ color: C.removed }}>-{s.removed}</span>
        </div>
        {changes.slice(0, 50).map((c, i) => (
          <div key={i} style={S.diffRow}>
            <span style={{ color: changeColor(c.type) }}>{c.type}</span> {c.section}{c.item_id ? ` · ${shortId(c.item_id)}` : ''}
          </div>
        ))}
        {changes.length > 50 && <div style={S.dim}>+{changes.length - 50} more…</div>}
      </div>
    </div>
  )
}

// ── Inline component: History tab ──────────────────────────────────────────
const HistoryTabContent = ({ checkpoints, loading, selectedId, comparing, onSelect, branchName, isPicked, togglePick }) => {
  const groups = groupCheckpointsByDate(checkpoints, Date.now())
  if (loading) return <div style={S.empty}>Loading…</div>
  if (checkpoints.length === 0 && !comparing) return <div style={S.empty}>No checkpoints on this branch yet.</div>
  // Compare-mode checkbox — PlayCanvas's orange "tick"; the whole row toggles.
  const Tick = ({ checked }) => (
    <span style={{
      width: 16, height: 16, borderRadius: 3, flex: 'none',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      border: `1px solid ${checked ? C.accent : C.borderInput}`,
      background: checked ? C.accent : 'transparent',
      color: '#000', fontSize: 11, fontWeight: 700, marginRight: 8,
    }}>{checked ? '✓' : ''}</span>
  )
  return (
    <>
      {comparing && (
        <div>
          <div style={S.dateHeader}>CURRENT STATE</div>
          <div
            style={{ ...S.row, ...(isPicked('current') ? S.rowPicked : {}) }}
            onClick={() => togglePick({ kind: 'current' })}
            data-testid="compare-current-state"
          >
            <Tick checked={isPicked('current')} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.cpLabel}>Working state of {branchName || 'branch'}</div>
              <div style={{ ...S.meta, color: C.accent }}>uncheckpointed changes</div>
            </div>
          </div>
        </div>
      )}
      {groups.map((g) => (
        <div key={g.label}>
          <div style={S.dateHeader}>{g.label}</div>
          {g.items.map((cp) => {
            const picked = comparing && isPicked(cp.id)
            return (
              <div
                key={cp.id}
                style={{
                  ...S.row,
                  ...(cp.id === selectedId && !comparing ? { borderLeft: `3px solid ${C.accent}` } : {}),
                  ...(picked ? S.rowPicked : {}),
                }}
                onClick={() => (comparing ? togglePick({ kind: 'checkpoint', cp }) : onSelect(cp))}
              >
                {comparing && <Tick checked={picked} />}
                <span style={{ ...S.avatar, background: colorForUser(cp.created_by || cp.id) }}>{initials(cp.created_by)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.cpLabel}>{cp.label || <span style={S.dim}>(no description)</span>}</div>
                  <div style={S.meta}>{cp.created_by || 'unknown'} · {fmtDate(cp.created_at)} {fmtTime(cp.created_at)}</div>
                </div>
                <span style={S.pill}>{shortId(cp.id)}</span>
              </div>
            )
          })}
        </div>
      ))}
    </>
  )
}

// ── Inline component: Detail pane for a selected checkpoint ──────────────────
const DetailPane = ({ characterId, checkpoint, branch, onRestore, onHardReset, onNewBranch }) => {
  const [diffChanges, setDiffChanges] = useState(null)
  const [diffLoading, setDiffLoading] = useState(false)

  useEffect(() => {
    if (!checkpoint) return
    setDiffLoading(true)
    void (async () => {
      try {
        const changes = checkpoint.parent_id
          ? await diffCheckpoints(characterId, checkpoint.parent_id, checkpoint.id)
          : null
        setDiffChanges(Array.isArray(changes) ? changes : [])
      } catch (e) {
        setDiffChanges(null)
      } finally {
        setDiffLoading(false)
      }
    })()
  }, [checkpoint?.id, characterId])

  if (!checkpoint) return null
  return (
    <div style={{ width: 340, flexShrink: 0, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: 14, borderBottom: `1px solid ${C.border}`, overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
          <span style={{ ...S.avatar, background: colorForUser(checkpoint.created_by || checkpoint.id) }}>{initials(checkpoint.created_by)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{checkpoint.label || <span style={S.dim}>(no description)</span>}</div>
            <div style={S.meta}>{checkpoint.created_by || 'unknown'} · {fmtDate(checkpoint.created_at)} {fmtTime(checkpoint.created_at)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
          <span style={S.pill}>{shortId(checkpoint.id)}</span>
          <button
            style={{ ...S.iconBtn, fontSize: 10, padding: '1px 4px' }}
            title="Copy checkpoint ID"
            onClick={() => navigator.clipboard.writeText(checkpoint.id)}
          >
            📋
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button style={S.primary} onClick={() => onRestore(checkpoint)}>Restore</button>
          <button style={S.ghost} onClick={() => onNewBranch(checkpoint)}>New Branch</button>
          <button style={S.danger} onClick={() => onHardReset(checkpoint)}>Hard Reset…</button>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <div style={{ fontSize: 11, padding: '8px 14px', color: C.textDim, fontWeight: 500, borderBottom: `1px solid ${C.border}` }}>
          Changes in this checkpoint
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 14 }}>
          {diffLoading && <div style={S.dim}>Loading…</div>}
          {!diffLoading && diffChanges === null && (
            <div style={S.dim}>This is the oldest loaded checkpoint — scroll the history to load older ones for a comparison baseline.</div>
          )}
          {!diffLoading && diffChanges !== null && diffChanges.length === 0 && (
            <div style={S.dim}>No changes in this checkpoint.</div>
          )}
          {!diffLoading && diffChanges && diffChanges.length > 0 && (
            <div style={S.diffBox}>
              {(() => {
                const s = { added: 0, modified: 0, removed: 0 }
                for (const c of diffChanges) if (s[c.type] != null) s[c.type] += 1
                return (
                  <>
                    <div style={{ marginBottom: 6, fontSize: 10 }}>
                      <span style={{ color: C.added }}>+{s.added}</span> {' '}
                      <span style={{ color: C.modified }}>~{s.modified}</span> {' '}
                      <span style={{ color: C.removed }}>-{s.removed}</span>
                    </div>
                    {diffChanges.slice(0, 30).map((c, i) => (
                      <div key={i} style={S.diffRow}>
                        <span style={{ color: changeColor(c.type) }}>{c.type}</span> {c.section}{c.item_id ? ` · ${shortId(c.item_id)}` : ''}
                      </div>
                    ))}
                    {diffChanges.length > 30 && <div style={S.dim}>+{diffChanges.length - 30} more…</div>}
                  </>
                )
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}


export default function VersionPanel({ characterId, onClose }) {
  const [branches, setBranches] = useState([])
  const [branchId, setBranchId] = useState(null)
  const [checkpoints, setCheckpoints] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null) // transient success feedback
  const [busy, setBusy] = useState(false)

  const [flow, setFlow] = useState(null)   // { type, ...payload }
  const [graph, setGraph] = useState(null) // { title, checkpoints, branches, itemHistory }
  const [comparing, setComparing] = useState(false)
  const [comparePick, setComparePick] = useState([])
  const [selectedCheckpointId, setSelectedCheckpointId] = useState(null)
  const [activeTab, setActiveTab] = useState('history') // 'history' | 'changes'
  const [changesUncheckpointed, setChangesUncheckpointed] = useState(null)

  const branch = branches.find((b) => b.id === branchId) || null

  const loadCheckpoints = useCallback(async (bid) => {
    const cps = bid ? await listCheckpoints(characterId, bid) : []
    setCheckpoints(Array.isArray(cps) ? cps : [])
  }, [characterId])

  const reload = useCallback(async (preferBranchId) => {
    setLoading(true); setError(null)
    try {
      const brs = await listBranches(characterId)
      const list = Array.isArray(brs) ? brs : []
      setBranches(list)
      const chosen =
        (preferBranchId && list.find((b) => b.id === preferBranchId)?.id) ||
        (branchId && list.find((b) => b.id === branchId)?.id) ||
        list.find((b) => b.permanent)?.id || list[0]?.id || null
      setBranchId(chosen)
      await loadCheckpoints(chosen)
    } catch (e) {
      setError(e?.message || 'Failed to load version control')
    } finally {
      setLoading(false)
    }
  }, [characterId, branchId, loadCheckpoints])

  useEffect(() => { void reload() }, [characterId]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectBranch = async (b) => {
    setBranchId(b.id); setFlow(null); setComparing(false); setComparePick([]); setSelectedCheckpointId(null); setActiveTab('history')
    setBusy(true)
    try { await loadCheckpoints(b.id) }
    catch (e) { setError(e?.message || 'Failed to load branch') }
    finally { setBusy(false) }
  }

  // Load uncheckpointed changes (Changes tab)
  const loadUncheckpointedChanges = async () => {
    const head = branch?.latest_checkpoint_id
    if (!head) { setChangesUncheckpointed([]); return }
    try {
      const ch = await getChanges(characterId, head)
      setChangesUncheckpointed(Array.isArray(ch) ? ch : [])
    } catch (e) {
      setError(e?.message || 'failed to load changes')
    }
  }

  // Load the diff for a selected checkpoint (right detail pane)
  const loadCheckpointDiff = async (cpId) => {
    const cp = checkpoints.find((c) => c.id === cpId)
    if (!cp) return
    setSelectedCheckpointId(cpId)
    // The diff will be loaded on demand in the detail pane
  }

  // Wrap a mutating action: run, reload, surface errors loudly, close flow.
  // `fn` may return a branch id to reload to (e.g. a freshly created branch);
  // otherwise `reloadTo` is used, falling back to the current branch.
  const run = async (fn, { reloadTo, reloadMaterials, notice: noticeText } = {}) => {
    setBusy(true); setError(null)
    try {
      const preferId = await fn()
      await reload(preferId || reloadTo)
      if (reloadMaterials) window.__RELOAD_MATERIALS__?.()
      setFlow(null)
      // Success feedback — actions completed silently before, which read as
      // "nothing happened" (especially when triggered from the graph modal).
      if (noticeText) {
        setNotice(noticeText)
        setTimeout(() => setNotice((n) => (n === noticeText ? null : n)), 4000)
      }
    } catch (e) {
      setError(e?.message || 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  // ── Branch actions ─────────────────────────────────────────────────────────
  const openNewBranch = (sourceCheckpoint) => {
    setFlow({
      type: 'newbranch',
      sourceCheckpoint,
      source: sourceCheckpoint
        ? { label: `checkpoint ${shortId(sourceCheckpoint.id)}${sourceCheckpoint.label ? ` — ${sourceCheckpoint.label}` : ''}` }
        : { label: `branch "${branch?.name}"` },
    })
  }
  const confirmNewBranch = (name) => run(async () => {
    const body = { name }
    if (flow.sourceCheckpoint) body.source_checkpoint_id = flow.sourceCheckpoint.id
    else if (branchId) body.source_branch_id = branchId
    const created = await createBranch(characterId, body)
    return created?.id
  })

  const openMerge = async (sourceBranch) => {
    setFlow({ type: 'merge', source: sourceBranch, dest: branch, preview: null, error: null })
    try {
      const preview = await previewMerge(characterId, sourceBranch.id, branchId)
      setFlow((f) => (f && f.type === 'merge' ? { ...f, preview } : f))
    } catch (e) {
      setFlow((f) => (f && f.type === 'merge' ? { ...f, error: e?.message || 'merge preview failed' } : f))
    }
  }
  const applyMergeNow = (resolutions = []) => run(
    () => applyMerge(characterId, flow.source.id, flow.dest.id, resolutions),
    { reloadMaterials: true },
  )

  const toggleClosed = (b) => run(() => (b.closed ? openBranch(characterId, b.id) : closeBranch(characterId, b.id)))
  const removeBranch = (b) => {
    if (!window.confirm(`Delete branch "${b.name}"? This cannot be undone.`)) return
    run(() => deleteBranch(characterId, b.id), { reloadTo: branches.find((x) => x.permanent)?.id })
  }

  // ── Checkpoint actions ──────────────────────────────────────────────────────
  const openNewCheckpoint = async () => {
    setFlow({ type: 'newcheckpoint', changes: null, changesErr: null })
    const head = branch?.latest_checkpoint_id
    if (!head) { setFlow((f) => (f?.type === 'newcheckpoint' ? { ...f, changes: [] } : f)); return }
    try {
      const ch = await getChanges(characterId, head)
      setFlow((f) => (f?.type === 'newcheckpoint' ? { ...f, changes: Array.isArray(ch) ? ch : [] } : f))
    } catch (e) {
      setFlow((f) => (f?.type === 'newcheckpoint' ? { ...f, changesErr: e?.message || 'failed to compute changes' } : f))
    }
  }
  const confirmNewCheckpoint = (label) => run(
    () => createCheckpoint(characterId, { label, branch: branch?.name }),
    { reloadTo: branchId },
  )

  const confirmRestore = () => run(() => restoreCheckpoint(characterId, flow.cp.id), {
    reloadTo: branchId, reloadMaterials: true,
    notice: `Restored checkpoint ${shortId(flow.cp.id)} ✓ (a backup of the previous state was created)`,
  })
  const confirmHardReset = () => run(() => hardResetCheckpoint(characterId, flow.cp.id), {
    reloadTo: branchId, reloadMaterials: true,
    notice: `Hard reset to ${shortId(flow.cp.id)} ✓`,
  })

  const openViewChanges = async (cp) => {
    setFlow({ type: 'diff', title: `Changes in ${shortId(cp.id)}`, from: { id: cp.parent_id }, to: cp, changes: null, error: null })
    try {
      const ch = await diffCheckpoints(characterId, cp.parent_id, cp.id)
      setFlow((f) => (f?.type === 'diff' ? { ...f, changes: Array.isArray(ch) ? ch : [] } : f))
    } catch (e) {
      setFlow((f) => (f?.type === 'diff' ? { ...f, error: e?.message || 'diff failed' } : f))
    }
  }

  // ── Compare (PlayCanvas contract) ──────────────────────────────────────────
  // Compare mode: checkboxes on every history row plus a "current working
  // state" row; picks fill the LEFT slot first, then the RIGHT (a third pick
  // replaces the right — mirrors PlayCanvas onCheckpointSelected). Nothing
  // diffs until the explicit COMPARE button in the bottom bar is pressed.
  // A pick is { kind: 'checkpoint', cp } or { kind: 'current' }.
  const toggleCompare = () => {
    setComparing((v) => !v)
    setComparePick([])
    setFlow((f) => (f?.type === 'diff' ? null : f))
  }
  const pickKey = (p) => (p.kind === 'current' ? 'current' : p.cp.id)
  const isPicked = (key) => comparePick.some((p) => pickKey(p) === key)
  const togglePick = (pick) => {
    const key = pickKey(pick)
    setComparePick((prev) => {
      if (prev.some((p) => pickKey(p) === key)) return prev.filter((p) => pickKey(p) !== key)
      if (prev.length === 0) return [pick]           // fills LEFT
      return [prev[0], pick]                          // fills/replaces RIGHT
    })
  }
  const clearPickSlot = (i) => setComparePick((prev) => prev.filter((_, idx) => idx !== i))
  const swapPicks = () => setComparePick((prev) => (prev.length === 2 ? [prev[1], prev[0]] : prev))

  const runCompare = async () => {
    if (comparePick.length !== 2) return
    const [a, b] = comparePick
    const slotLabel = (p) => (p.kind === 'current' ? 'Working state' : shortId(p.cp.id))
    const title = `Comparing ${slotLabel(a)} ⇆ ${slotLabel(b)}`
    setFlow({ type: 'diff', title, from: { id: a.kind === 'current' ? null : a.cp.id }, to: { id: b.kind === 'current' ? null : b.cp.id }, changes: null, error: null })
    try {
      let ch
      if (a.kind === 'current' || b.kind === 'current') {
        // Working state vs a checkpoint: getChanges diffs a checkpoint
        // against the branch's current live state.
        const cp = a.kind === 'current' ? b.cp : a.cp
        ch = await getChanges(characterId, cp.id)
      } else {
        // Two checkpoints: oldest first so the diff reads forward in time.
        const older = checkpoints.indexOf(a.cp) > checkpoints.indexOf(b.cp) ? a.cp : b.cp
        const newer = older === a.cp ? b.cp : a.cp
        ch = await diffCheckpoints(characterId, older.id, newer.id)
      }
      setFlow((f) => (f?.type === 'diff' ? { ...f, changes: Array.isArray(ch) ? ch : [] } : f))
    } catch (e) {
      setFlow((f) => (f?.type === 'diff' ? { ...f, error: e?.message || 'compare failed' } : f))
    }
  }

  // Main graph reads LIVE checkpoints/branches (not a snapshot) so restores /
  // new checkpoints appear without reopening the modal.
  const openGraph = () => setGraph({ title: `Graph — ${branch?.name || ''}`, live: true })

  // Flows render wherever the user acted: inside the graph modal when it is
  // open (it covers the panel — a hidden confirm read as "nothing happened"),
  // else in the side panel as before.
  const flowUI = (
    <>
      {flow?.type === 'restore' && (
        <RestoreFlow cp={flow.cp} busy={busy} onCancel={() => setFlow(null)} onConfirm={confirmRestore} />
      )}
      {flow?.type === 'hardreset' && (
        <HardResetFlow cp={flow.cp} branch={branch} busy={busy} onCancel={() => setFlow(null)} onConfirm={confirmHardReset} />
      )}
      {flow?.type === 'newbranch' && (
        <NewBranchFlow source={flow.source} busy={busy} onCancel={() => setFlow(null)} onConfirm={confirmNewBranch} />
      )}
      {flow?.type === 'newcheckpoint' && (
        <NewCheckpointFlow changes={flow.changes} changesErr={flow.changesErr} busy={busy} onCancel={() => setFlow(null)} onConfirm={confirmNewCheckpoint} />
      )}
      {flow?.type === 'diff' && (
        <DiffFlow title={flow.title} from={flow.from} to={flow.to} changes={flow.changes} error={flow.error} busy={busy} onCancel={() => setFlow(null)} />
      )}
      {flow?.type === 'merge' && (
        <MergeFlow source={flow.source} dest={flow.dest} preview={flow.preview} error={flow.error} busy={busy} onCancel={() => setFlow(null)} onApply={applyMergeNow} />
      )}
    </>
  )

  return (
    // A WINDOW, not a modal. It was an opaque box behind a dimming backdrop,
    // with a hand-rolled Maximize/Restore — i.e. it was already reimplementing
    // window controls, badly: sized in `vh`, which is NOT divided by the
    // UI-scale CSS zoom, so it overflowed the viewport at any scale above 1.
    // FloatingWindow supplies drag, resize, persisted geometry and
    // pop-out-to-a-second-monitor, so the hand-rolled maximize is gone rather
    // than duplicated. Comparing a checkpoint against the thing it changed is
    // the whole job, and the backdrop hid it.
    <FloatingWindow
      id="version-control"
      title="VERSION CONTROL"
      onClose={onClose}
      dockable={false}
      defaultSize={{ w: 1120, h: 720 }}
      barExtra={<span style={S.dim}>{characterId}</span>}
    >

        {error && <div style={S.error}>⚠ {error}</div>}
        {notice && <div style={{ ...S.error, color: '#7fd18b', borderColor: '#2c4a33' }}>{notice}</div>}

        <div style={S.body}>
          {/* Main pane: branch header + tabs + checkpoint list */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={S.headerMain}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: C.textDim }}>CURRENT BRANCH</span>
                <BranchDropdown
                  branches={branches}
                  currentBranchId={branchId}
                  busy={busy}
                  onSelect={selectBranch}
                  onNewBranch={openNewBranch}
                  onMerge={openMerge}
                  onToggleClosed={toggleClosed}
                  onDelete={removeBranch}
                />
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button style={S.ghost} onClick={openGraph} title="Version-control graph">⑂ Graph</button>
                <button style={comparing ? S.primary : S.ghost} onClick={toggleCompare}>{comparing ? 'Exit Compare' : '<> Compare'}</button>
                <button style={S.primary} disabled={busy} onClick={openNewCheckpoint}>+ Checkpoint</button>
              </div>
            </div>

            <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}` }}>
              <button
                style={{
                  flex: 'none',
                  background: 'transparent',
                  border: 'none',
                  color: activeTab === 'changes' ? C.accent : C.textDim,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  fontSize: 12,
                  borderBottom: activeTab === 'changes' ? `2px solid ${C.accent}` : '2px solid transparent',
                }}
                onClick={() => { setActiveTab('changes'); void loadUncheckpointedChanges() }}
              >
                Changes
              </button>
              <button
                style={{
                  flex: 'none',
                  background: 'transparent',
                  border: 'none',
                  color: activeTab === 'history' ? C.accent : C.textDim,
                  padding: '8px 14px',
                  cursor: 'pointer',
                  fontSize: 12,
                  borderBottom: activeTab === 'history' ? `2px solid ${C.accent}` : '2px solid transparent',
                }}
                onClick={() => setActiveTab('history')}
              >
                History
              </button>
            </div>

            <div style={S.scroll}>
              {activeTab === 'changes' && <ChangesTabContent changes={changesUncheckpointed} loading={loading} />}
              {activeTab === 'history' && (
                <HistoryTabContent
                  checkpoints={checkpoints}
                  loading={loading}
                  selectedId={selectedCheckpointId}
                  comparing={comparing}
                  // HistoryTabContent hands back the checkpoint OBJECT;
                  // loadCheckpointDiff keys on the id. Passing the object
                  // made `checkpoints.find(c => c.id === cp)` never match, so
                  // row clicks silently did nothing and the detail pane
                  // never opened.
                  onSelect={(cp) => loadCheckpointDiff(cp.id)}
                  branchName={branch?.name}
                  isPicked={isPicked}
                  togglePick={togglePick}
                />
              )}
            </div>

            {/* PlayCanvas compare bar: two slots ⇆ + explicit COMPARE button. */}
            {comparing && activeTab === 'history' && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                borderTop: `1px solid ${C.borderSoft}`, background: C.panelAlt || C.panel,
              }}>
                {[0, 1].map((i) => {
                  const p = comparePick[i]
                  const label = p ? (p.kind === 'current' ? `Working state · ${branch?.name || 'branch'}` : `${shortId(p.cp.id)} · ${branch?.name || ''}`) : null
                  return (
                    <div key={i} style={{
                      flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6,
                      border: `1px solid ${C.borderInput}`, borderRadius: 4, padding: '7px 10px',
                      color: p ? C.text : C.textDim, fontSize: 12,
                    }}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label || "Select a checkpoint or the current state"}
                      </span>
                      {p && (
                        <button
                          style={{ background: 'transparent', border: 'none', color: C.textDim, cursor: 'pointer' }}
                          title="Clear" onClick={() => clearPickSlot(i)}
                        >✕</button>
                      )}
                    </div>
                  )
                })}
                <button
                  style={{ background: 'transparent', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 14 }}
                  title="Swap" onClick={swapPicks} disabled={comparePick.length !== 2}
                >⇆</button>
                <button
                  style={{ ...S.primary, opacity: comparePick.length === 2 ? 1 : 0.45 }}
                  disabled={comparePick.length !== 2}
                  onClick={runCompare}
                >
                  Compare
                </button>
              </div>
            )}
          </div>

          {/* Detail pane: selected checkpoint, or the compare hint */}
          {activeTab === 'history' && selectedCheckpointId && !comparing && (
            <DetailPane
              characterId={characterId}
              checkpoint={checkpoints.find((c) => c.id === selectedCheckpointId)}
              branch={branch}
              onRestore={(cp) => setFlow({ type: 'restore', cp })}
              onHardReset={(cp) => setFlow({ type: 'hardreset', cp })}
              onNewBranch={(cp) => openNewBranch(cp)}
            />
          )}
          {activeTab === 'history' && comparing && (
            <div style={{ width: 340, flex: 'none', borderLeft: `1px solid ${C.borderSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <span style={S.dim}>
                {comparePick.length < 2
                  ? 'Select two items to compare, then press Compare.'
                  : 'Press Compare to see the differences.'}
              </span>
            </div>
          )}

          {/* Side-panel flows slide over content (hidden while the
              graph modal is open — the flows render inside it instead). */}
          {!graph && flowUI}
        </div>

      {graph && (
        <div style={S.graphModal} onMouseDown={(e) => e.stopPropagation()}>
          <div style={S.graphHeader}>
            <span style={S.title}>{graph.title}</span>
            {notice && <span style={{ color: '#7fd18b', fontSize: 12, marginLeft: 14 }}>{notice}</span>}
            {error && <span style={{ color: C.danger, fontSize: 12, marginLeft: 14 }}>⚠ {error}</span>}
            <button style={{ ...S.iconBtn, marginLeft: 'auto' }} title="Close graph" onClick={() => setGraph(null)}>✕</button>
          </div>
          {/* Actions triggered from graph nodes confirm HERE, where the user
              acted — previously the confirm rendered in the panel underneath
              the modal, i.e. invisible. */}
          {flow && <div style={{ position: 'relative' }}>{flowUI}</div>}
          <div style={S.graphCanvas}>
            <GraphView
              checkpoints={graph.live ? checkpoints : graph.checkpoints}
              branches={graph.live ? branches : graph.branches}
              itemHistory={graph.itemHistory}
              selectedId={null}
              onSelect={() => {}}
              onRestore={(cp) => setFlow({ type: 'restore', cp })}
              onHardReset={(cp) => setFlow({ type: 'hardreset', cp })}
              onNewBranch={(cp) => openNewBranch(cp)}
              onViewChanges={(cp) => openViewChanges(cp)}
              onCompare={(baseNode, targetNode) => {
                // Note: We lack a 2-checkpoint diff endpoint; fall back to single-checkpoint View Changes on target
                // with a code comment noting the limitation (see implementation note below)
                openViewChanges(targetNode)
              }}
            />
          </div>
        </div>
      )}
    </FloatingWindow>
  )
}
